<template>
  <div>
    <SiteBanner :site-address="siteAddress" :site-name="siteName" />
    <WorkOrderDetails v-if="hasSelectedWorkOrder" class="mt-4" />
    <div class="p-4">
      <h2 class="mx-4 mb-4 mt-2 text-center text-lg font-medium">
        {{ serviceName }}
      </h2>
      <div v-if="frequencies.length > 0 || hasReactiveMaintenance">
        <h4 class="my-3">Please select one or more frequencies</h4>
        <div
          v-for="frequency in frequencies"
          :key="frequencies.indexOf(frequency)"
          class="relative mb-3"
        >
          <input
            :id="'frequency' + '-' + frequency.id"
            v-model="selectedFrequency"
            type="checkbox"
            class="rounded-sm"
            :value="frequency.id"
            @change="newOptionSelected"
          />
          <label :for="'frequency' + '-' + frequency.id" class="pl-3">
            {{ frequency.description }}
          </label>
          <span
            v-if="frequencyCompletedBefore(frequency.id)"
            class="absolute right-2 top-1 text-blue-500"
          >
            <CheckCircleIcon class="h-5 w-5" />
          </span>
        </div>
        <div v-if="hasReactiveMaintenance" class="relative mb-3">
          <input
            id="frequency-repair"
            v-model="selectedFrequency"
            type="checkbox"
            class="rounded-sm"
            :value="repairFrequencyId"
            @change="newOptionSelected"
          />
          <label for="frequency-repair" class="pl-3">Repair</label>
          <span
            v-if="frequencyCompletedBefore(repairFrequencyId)"
            class="absolute right-2 top-1 text-blue-500"
          >
            <CheckCircleIcon class="h-5 w-5" />
          </span>
        </div>
        <div class="mt-7">
          <PrimaryButton
            :key="continueBtnKey"
            type="button"
            :disabled="selectedFrequency?.length === 0"
            action="CONTINUE"
            identifier="confirmFrequencies"
            @click.once="checkForDuplicateAndContinue"
          />
        </div>
      </div>
      <div v-else>
        <p class="mx-2 my-6 text-center">
          There are no valid service intervals, please select another service
        </p>
        <PrimaryButton action="OK" @click="backToServiceSelectionPage" />
      </div>
    </div>
    <DuplicateServiceModal
      :show-duplicate-service-modal="showDuplicateServiceModal"
      @close="closeDuplicateServiceModal"
      @confirm="confirmFrequencies"
      @open-history="openMyHistory"
    />
    <BaseModal
      v-if="shouldShowSiblingFrequencyPopup"
      title="Confirm Frequencies"
      @close="cancelSiblingFrequencies"
    >
      <div>
        <p>
          Are you completing the
          <span class="font-medium">
            {{
              siblingFrequencies
                .map((sibling: Frequency) => sibling.description)
                .join(', ')
            }}
          </span>
          frequency(s) as well?
        </p>
        <div class="mt-4 flex gap-1">
          <SecondaryButton action="NO" @click="cancelSiblingFrequencies" />
          <PrimaryButton action="YES" @click="confirmSiblingFrequencies" />
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script lang="ts">
  import { computed, defineComponent, ref } from 'vue'
  import PrimaryButton from '@/components/PrimaryButton.vue'
  import { useRouter } from 'vue-router'
  import { Frequency } from '@/services/api/serviceFrequency'
  import { CheckCircleIcon } from '@heroicons/vue/24/solid'
  import SiteBanner from '@/components/SiteBanner.vue'
  import DuplicateServiceModal from '@/components/DuplicateServiceModal.vue'
  import overlay from '@/components/use/overlay'
  import UserHistory from '@/views/UserHistory.vue'
  import WorkOrderDetails from '@/components/WorkOrderDetails.vue'
  import { storeToRefs } from 'pinia'
  import { useFrequenciesStore } from '@/storage/frequencies'
  import { useServicesStore } from '@/storage/services'
  import { useSiteStore } from '@/storage/site'
  import { useWorkOrderStore } from '@/storage/workOrder'
  import BaseModal from '@/components/BaseModal.vue'
  import SecondaryButton from '@/components/SecondaryButton.vue'

  export default defineComponent({
    name: 'ServiceFrequency',
    components: {
      BaseModal,
      CheckCircleIcon,
      DuplicateServiceModal,
      PrimaryButton,
      SecondaryButton,
      SiteBanner,
      WorkOrderDetails,
    },

    async setup() {
      const { address: siteAddress, name: siteName } =
        storeToRefs(useSiteStore())
      const { setSelectedFrequency } = useFrequenciesStore()
      const { previouslyCompleted } = storeToRefs(useFrequenciesStore())
      const {
        selectedService,
        hasReactiveMaintenance,
        selectedServiceSiteServiceId,
        selectedServiceClientServiceId,
        serviceName,
      } = storeToRefs(useServicesStore())
      const router = useRouter()
      const { hasSelectedWorkOrder, selectedWorkOrderType } =
        storeToRefs(useWorkOrderStore())

      const repairFrequencyId = 34
      const canContinue = ref(false)
      const continueBtnKey = ref(0)
      const selectedFrequency = ref<number[]>([])
      const frequencies = ref<Frequency[]>([])
      const showDuplicateServiceModal = ref<boolean>(false)
      const frequencySiblingMap = computed(() => {
        const map = new Map()

        if (
          !selectedService.value ||
          !('siblingFrequencies' in selectedService.value) ||
          !Array.isArray(selectedService.value.siblingFrequencies) ||
          selectedService.value.siblingFrequencies.length === 0
        ) {
          return map
        }

        selectedService.value.siblingFrequencies.forEach((sibling) => {
          if (!map.has(sibling.frequencyId)) {
            map.set(sibling.frequencyId, [])
          }

          map.get(sibling.frequencyId).push(sibling.siblingFrequencyId)
        })

        return map
      })
      const shouldShowSiblingFrequencyPopup = ref(false)
      const siblingFrequencies = ref<Frequency[]>([])

      await setFrequencies()
      checkIfSkipPage()

      async function setFrequencies(): Promise<void> {
        if (!selectedService.value) {
          throw new Error(
            'selectedService.value is undefined in fetchSiteServiceFrequencies',
          )
        }
        frequencies.value = selectedService.value.frequencies

        filterFrequenciesForWorkOrder()
      }

      function filterFrequenciesForWorkOrder() {
        if (hasSelectedWorkOrder.value) {
          // TODO: what are we doing here...
          frequencies.value = frequencies.value.filter((frequency) => {
            if (selectedWorkOrderType.value === 'REACTIVE') {
              return frequency.description === 'Repair' // not a thing anymore, 'canRepair' on the clientService
            }
            return frequency.description !== 'Repair'
          })
        }
      }

      async function confirmFrequencies(): Promise<void> {
        continueBtnKey.value += 1
        setSelectedFrequency(selectedFrequency.value)
        await router.replace({ name: 'serviceForm' })
      }

      function newOptionSelected(event: Event | null): void {
        const frequencyId = (event?.target as HTMLInputElement)?.value
        if (parseInt(frequencyId) === repairFrequencyId) {
          selectedFrequency.value = [repairFrequencyId]
        } else if (selectedFrequency.value.includes(repairFrequencyId)) {
          selectedFrequency.value.splice(
            selectedFrequency.value.indexOf(repairFrequencyId),
          )
          selectedFrequency.value.push(parseInt(frequencyId))
        }

        findFrequencySiblings()
      }

      function frequencyCompletedBefore(frequencyId: number) {
        const completionRecExists = previouslyCompleted.value
          .filter(
            (record) =>
              record.siteServiceId === selectedServiceSiteServiceId.value &&
              record.clientServiceId === selectedServiceClientServiceId.value,
          )
          .find((record) => record.frequencyId.includes(frequencyId))

        return !!completionRecExists
      }

      function checkIfSkipPage() {
        if (frequencies.value.length === 1 && !hasReactiveMaintenance.value) {
          selectedFrequency.value = [frequencies.value[0].id]
          checkForDuplicateAndContinue()
        }

        if (frequencies.value.length === 0 && hasReactiveMaintenance.value) {
          selectedFrequency.value = [repairFrequencyId]
          checkForDuplicateAndContinue()
        }
      }

      function serviceIsCompleted(): boolean {
        return selectedFrequency.value.every((frequency) =>
          frequencyCompletedBefore(frequency),
        )
      }

      function checkForDuplicateAndContinue() {
        continueBtnKey.value += 1
        if (serviceIsCompleted()) {
          showDuplicateServiceModal.value = true
        } else {
          confirmFrequencies()
        }
      }

      function openMyHistory() {
        showDuplicateServiceModal.value = false
        overlay().setOverlayComponent(UserHistory)
      }

      function backToServiceSelectionPage() {
        router.replace({ name: 'serviceSelection' })
      }

      function closeDuplicateServiceModal() {
        showDuplicateServiceModal.value = false
      }

      function findFrequencySiblings() {
        if (
          frequencySiblingMap.value.size === 0 ||
          selectedFrequency.value.length === 0 ||
          selectedFrequency.value.length > 1
        ) {
          return
        }

        const siblingIds = frequencySiblingMap.value.get(
          selectedFrequency.value[0],
        )

        if (siblingIds === undefined || siblingIds.length === 0) {
          return
        }

        siblingIds.forEach((siblingId: number) => {
          const frequency = frequencies.value.find(
            (frequency) => frequency.id === siblingId,
          )
          if (frequency) {
            siblingFrequencies.value.push(frequency)
          }
        })
        shouldShowSiblingFrequencyPopup.value = true
      }

      function confirmSiblingFrequencies() {
        siblingFrequencies.value.forEach((sibling) => {
          selectedFrequency.value.push(sibling.id)
        })
        siblingFrequencies.value = []
        shouldShowSiblingFrequencyPopup.value = false
      }

      function cancelSiblingFrequencies() {
        siblingFrequencies.value = []
        shouldShowSiblingFrequencyPopup.value = false
      }

      return {
        backToServiceSelectionPage,
        canContinue,
        cancelSiblingFrequencies,
        checkForDuplicateAndContinue,
        closeDuplicateServiceModal,
        confirmFrequencies,
        confirmSiblingFrequencies,
        continueBtnKey,
        frequencies,
        frequencyCompletedBefore,
        hasReactiveMaintenance,
        hasSelectedWorkOrder,
        newOptionSelected,
        openMyHistory,
        repairFrequencyId,
        router,
        selectedFrequency,
        selectedService,
        serviceName,
        shouldShowSiblingFrequencyPopup,
        showDuplicateServiceModal,
        siblingFrequencies,
        siteAddress,
        siteName,
      }
    },
  })
</script>
